<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { HTMLAttributes, InjectionKey, Ref } from 'vue'
import { cva } from 'class-variance-authority'

export const inputGroupVariants = cva(
  [
    'group/input-group flex w-full min-w-0 items-center rounded-lg bg-white',
    'text-primal font-display transition-colors',
    'ring-1 ring-inset ring-neutral-600',
    'has-[input:focus]:ring-2 has-[input:focus]:ring-primary-500',
    '[&:not(:focus-within):not(:has(input[readonly])):not(:has(input:disabled)):not([data-disabled]):hover]:bg-neutral-600/10',
    'has-[input[readonly]]:bg-neutral-600/30 has-[input[readonly]]:text-gray-600 has-[input[readonly]]:not-focus-within:ring-neutral-600/60',
    'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-neutral-600/30 has-[input:disabled]:ring-0 has-[input:disabled]:text-neutral-900/30 has-[input:disabled]:**:pointer-events-none',
    'data-disabled:cursor-not-allowed data-disabled:bg-neutral-600/30 data-disabled:ring-0 data-disabled:text-neutral-900/30 data-disabled:**:pointer-events-none',
  ].join(' '),
  {
    variants: {
      size: {
        xs: 'h-6 gap-1 px-2 text-xs',
        sm: 'h-8 gap-1 px-2 text-xs',
        md: 'h-10 gap-2 px-2 text-base',
        lg: 'h-[52px] gap-2 px-3 text-base',
        xl: 'h-[60px] gap-2 px-4 text-lg',
      },
      error: {
        true: 'ring-error!',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

export type InputGroupVariants = VariantProps<typeof inputGroupVariants>
export type InputGroupSize = NonNullable<InputGroupVariants['size']>

export interface InputGroupProps {
  class?: HTMLAttributes['class']
  size?: InputGroupVariants['size']
  error?: boolean | string
  disabled?: boolean
}

export interface InputGroupContext {
  size: Ref<InputGroupSize>
  disabled: Ref<boolean>
  error: Ref<boolean>
}

export const INPUT_GROUP_KEY: InjectionKey<InputGroupContext> = Symbol('input-group')
</script>

<script setup lang="ts">
import { computed, provide } from 'vue'
import { cn } from '#ui-kit/lib/utils'

const props = withDefaults(defineProps<InputGroupProps>(), {
  size: 'md',
})

const size = computed<InputGroupSize>(() => props.size ?? 'md')
const disabled = computed(() => props.disabled ?? false)
const error = computed(() => !!props.error)

provide(INPUT_GROUP_KEY, { size, disabled, error })
</script>

<template>
  <div
    role="group"
    :data-size="size"
    :data-disabled="disabled || undefined"
    :class="cn(inputGroupVariants({ size, error }), props.class)"
  >
    <slot />
  </div>
</template>
